<template>
  <modal
    ref="modal"
    width="100%"
    height="100%"
    name="quick-quote"
    class="slide-modal slide-modal--header"
    overlay-transition="slide-modal-overlay"
    @before-open="beforeOpen"
    @before-close="beforeClose"
  >
    <!-- Shared chrome for every step: the panel scrolls underneath it, so back
         and close stay reachable however long a step gets. -->
    <div class="slide-modal-header">
      <button
        v-if="canGoBack"
        type="button"
        class="slide-modal-back"
        data-test="quick-quote-back"
        @click="headerBack"
      >
        <span class="slide-modal-arrow" aria-hidden="true">
          <svg viewBox="0 0 24 24">
            <path
              d="M20 12H4m0 0l6-6m-6 6l6 6"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </span>
        Back
      </button>
      <span v-else></span>

      <button
        type="button"
        class="slide-modal-close"
        data-test="quick-quote-close"
        @click="closeQuickQuote"
      >
        Close
        <span class="slide-modal-cross" aria-hidden="true">
          <svg viewBox="0 0 24 24">
            <path
              d="M6 6l12 12M18 6L6 18"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
            />
          </svg>
        </span>
      </button>
    </div>

    <div ref="scroller" class="slide-modal-scroll">
      <div class="slide-modal-container">
        <keep-alive>
          <RiskFactors
            v-if="isRiskFactors && !showLoginForm"
            :prefill="prefill"
            @continue="continueToPriceInfo"
            @personalCover="continueToPersonalCover"
          />
        </keep-alive>
        <keep-alive>
          <PersonalCoverRiskFactors
            v-if="!isRiskFactors && !showLoginForm && personalCover"
            @continue="continueToPersonalCoverQuoteProcess"
          />
        </keep-alive>
        <PriceInfo
          v-if="
            !isRiskFactors && !showLoginForm && !personalCover && !showRegister
          "
          ref="priceInfo"
          @continue="continueToQuoteProcess"
        />
        <RegisterStep
          v-if="showRegister"
          ref="registerStep"
          @registered="goToQuoteSetup"
        />
        <!-- <PersonalCover
          v-if="!isRiskFactors && !showLoginForm && !isPersonalCoverRiskFactors && personalCover"
          v-on:back="goBackToRickFactors"
          v-on:continue="continueToPersonalCoverQuoteProcess"
        /> -->
        <LoginForm v-if="showLoginForm" :redirect-url="redirectUrl()" />
      </div>
    </div>
  </modal>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import RiskFactors from '@/components/quick-quote/RiskFactors'
import PriceInfo from '@/components/quick-quote/PriceInfo'
import PersonalCoverRiskFactors from '@/components/quick-quote/PersonalCoverRiskFactors'
import RegisterStep from '@/components/quick-quote/RegisterStep'
import LoginForm from '@/components/auth/LoginForm'
import { lockBodyScroll, unlockBodyScroll } from '@/helpers/scrollLock'
import { normaliseBikeType, sanitiseSrc } from '@/helpers/estimator'

// The panel's own steps, in the order a customer meets them. These are the
// values that reach GA4 and the ?step= parameter, so they are part of the
// analytics contract — renaming one breaks reporting continuity.
//
// Note this is the real order: the instant-quote form comes first and the
// priced summary second. Any brief describing it the other way round is
// describing a flow this codebase does not have.
const STEP_DETAILS = 'details' // RiskFactors — "Get your instant quote"
const STEP_SUMMARY = 'summary' // PriceInfo   — the priced quote + what's in it
const STEP_ACCOUNT = 'account' // RegisterStep — "Continue securely"
const STEP_SIGNIN = 'signin' // LoginForm    — existing-customer branch
const STEP_PERSONAL_COVER = 'personal-cover' // the personal-cover-only branch

export default {
  name: 'QuickQuoteModal',
  components: {
    RiskFactors,
    PriceInfo,
    PersonalCoverRiskFactors,
    RegisterStep,
    LoginForm,
  },
  data() {
    return {
      isRiskFactors: true,
      showLoginForm: false,
      showRegister: false,
      personalCover: false,
      // Values the opening page already knows — the estimator's two sliders,
      // and the bike type when a landing page supplied one. They seed step 1's
      // fields; they are never treated as answers the customer has given.
      prefill: null,
      isOpen: false,
      // Which page sent the customer into the funnel, so a quote start can be
      // attributed back to the organic page that produced it.
      source: '',
      // How many history entries this panel has pushed. Unwound on close, so
      // the customer leaves on the URL they arrived on.
      pushedSteps: 0,
      // True while a browser Back press is being applied, so the step we land
      // on doesn't push a fresh entry for the one we just left.
      isPoppingHistory: false,
      // Set when a step deliberately routes elsewhere; stops the close handler
      // from unwinding history into the navigation that is already under way.
      isNavigatingAway: false,
    }
  },
  computed: {
    ...mapGetters({ quoteId: 'quote/quoteId' }),
    // Step 1 has nothing behind it.
    canGoBack() {
      return !this.isRiskFactors
    },
    // Every step transition in the panel is a change to one of these flags, so
    // deriving the step from them means tracking is wired up once rather than
    // added by hand to each of the eight places that move between steps.
    currentStep() {
      if (this.showLoginForm) return STEP_SIGNIN
      if (this.showRegister) return STEP_ACCOUNT
      if (this.personalCover) return STEP_PERSONAL_COVER
      if (this.isRiskFactors) return STEP_DETAILS

      return STEP_SUMMARY
    },
  },
  watch: {
    // The chat launcher would sit over the panel's CTAs.
    isOpen(open) {
      if (this.$chatLauncher) this.$chatLauncher.hideFor('quick-quote', open)
    },
    currentStep(step) {
      // Every step shares the one scroller, so without this a step opens at
      // the offset the last one was left at — on a phone, the price step
      // appeared already scrolled past its price.
      this.$nextTick(() => {
        if (this.$refs.scroller) this.$refs.scroller.scrollTop = 0
      })
      if (this.isOpen) {
        this.enterStep(step)
      }
    },
    // Several steps navigate away with the panel still open (personal cover,
    // the login redirect). Closing it here is what releases the body lock —
    // without this the next page arrives unscrollable.
    //
    // Keyed on the path, not the whole route: this panel's own ?step= entries
    // surface here after a Back press, and they must not close it.
    $route(to, from) {
      if (to.path === from.path) return
      this.isNavigatingAway = true
      this.$modal.hide('quick-quote')
    },
  },
  mounted() {
    window.addEventListener('popstate', this.onPopState)
  },
  beforeDestroy() {
    window.removeEventListener('popstate', this.onPopState)
    if (this.$chatLauncher) this.$chatLauncher.hideFor('quick-quote', false)
    unlockBodyScroll({ restoreScroll: false })
  },
  methods: {
    ...mapActions('quote', [
      'editRiskFactors',
      'editPersonalCoverRiskFactors',
      'createQuote',
      'createPersonalCoverQuote',
    ]),
    async continueToPriceInfo(riskFactor) {
      await this.editRiskFactors(riskFactor)
      // Created before the step switches: its response is the price (there is
      // no separate /price call), and PriceInfo, the GTM events and the step
      // tracking all read it as the summary step opens.
      await this.createQuote()
      this.isRiskFactors = false
    },
    goBackToRickFactors() {
      this.isRiskFactors = true
      // Leaving this set would send a later "continue" back into the personal
      // cover branch instead of the price step.
      this.personalCover = false
    },

    // The header's Back is shared by every step, so it unwinds whichever one is
    // on screen. A step that has its own internal history (the register step's
    // "check your inbox" screen) gets first refusal via `goBack()`.
    headerBack() {
      if (this.showLoginForm) {
        this.showLoginForm = false
        return
      }
      if (this.showRegister) {
        const step = this.$refs.registerStep
        if (step && step.goBack()) return
        this.showRegister = false
        return
      }
      if (this.personalCover) {
        this.goBackToRickFactors()
        return
      }
      const priceInfo = this.$refs.priceInfo
      if (priceInfo && priceInfo.goBack()) return
      this.goBack()
    },

    goBack() {
      this.goBackToRickFactors()
    },

    // --- Step tracking and shallow routing --------------------------------
    //
    // The panel keeps its own steps and its own design; all that is added here
    // is a URL and a GA4 event per step, so funnel drop-off is measurable and
    // the browser's Back button unwinds the panel instead of the page.

    enterStep(step) {
      this.trackStep(step, {
        navigation: this.isPoppingHistory ? 'back' : 'forward',
        ...this.stepPayload(step),
      })
      // A Back press has already moved the history entry for us.
      if (!this.isPoppingHistory) {
        this.pushStepUrl(step)
      }
    },

    // What is worth knowing about each step, taken from the quote as it stands
    // at that moment rather than from anything the caller passed in.
    stepPayload(step) {
      const bikeValue = Number(this.$store.getters['quote/firstBikeValue']) || 0
      // The engine returns pennies-precise floats (29.675520000000002). Round
      // to what the customer is actually shown, so the reported figure and the
      // one on screen are the same number.
      const round = (value) => Math.round(value * 100) / 100
      const price = this.$store.getters['quote/defaultPrice']

      if (step === STEP_DETAILS) {
        return {
          bike_value: (this.prefill && this.prefill.bikeValue) || bikeValue,
          age: (this.prefill && this.prefill.age) || null,
        }
      }
      if (step === STEP_SUMMARY) {
        return {
          bike_value: bikeValue,
          annual_price: round(price ? price.annual : 0),
          monthly_price: round(price ? price.monthly : 0),
        }
      }
      if (step === STEP_ACCOUNT) {
        // The figure the customer is told is held for 30 days, which is the
        // monthly price the step itself puts on screen.
        const chosen = this.$store.getters['quote/chosenPrice']
        return {
          bike_value: bikeValue,
          held_price: round(chosen ? chosen.monthly : 0),
          cover_type: this.$store.getters['quote/coverType'],
        }
      }

      return { bike_value: bikeValue }
    },

    trackStep(step, payload = {}) {
      this.$gtm.push({
        event: 'quote_modal_step',
        step,
        quote_source: this.source,
        // Present from the very first step when the customer arrived from a
        // bike-specific landing page, so a funnel can be split by bike type.
        bike_type: (this.prefill && this.prefill.bike) || null,
        ...payload,
      })
    },

    // Shallow only: pushState never re-runs the route, so the page underneath
    // is untouched and the panel keeps its state.
    pushStepUrl(step) {
      if (typeof window === 'undefined' || !window.history) return
      const query = { ...this.$route.query, step }
      const search = Object.keys(query)
        .filter((key) => query[key] !== undefined && query[key] !== null)
        .map(
          (key) =>
            `${encodeURIComponent(key)}=${encodeURIComponent(query[key])}`
        )
        .join('&')
      window.history.pushState(
        { quickQuoteStep: step },
        '',
        `${this.$route.path}?${search}`
      )
      this.pushedSteps += 1
    },

    // Back closes the panel one step at a time, and leaves the page entirely
    // only once there is no step left behind the current one.
    onPopState() {
      if (!this.isOpen) return
      if (this.pushedSteps > 0) {
        this.pushedSteps -= 1
      }
      this.isPoppingHistory = true
      if (this.canGoBack) {
        this.headerBack()
      } else {
        // The browser has already restored the entry URL, so there is nothing
        // left to unwind.
        this.isNavigatingAway = true
        this.$modal.hide('quick-quote')
      }
      this.$nextTick(() => {
        this.isPoppingHistory = false
      })
    },

    redirectUrl() {
      const defaultUrl = `/quote/${this.quoteId}/make`
      const personalCoverUrl = `/quote/${this.quoteId}/personal-cover`

      if (this.personalCover) {
        return personalCoverUrl
      } else {
        return defaultUrl
      }
    },
    async continueToQuoteProcess() {
      // A real (non-guest) session already exists → attach email + go straight
      // through. Otherwise gate on the register step so we capture an email.
      const isAuthed = this.$store.getters['user/isAuthenticated']
      const email = this.$store.getters['user/userEmail']
      if (isAuthed && email && email !== 'anonymous@pedalsure.com') {
        const quoteId = this.$store.getters['quote/quoteId']
        await this.$store.dispatch('quote/syncQuote')
        this.goToQuoteSetup(quoteId)
        return
      }
      this.showRegister = true
    },
    goToQuoteSetup(quoteId) {
      const id = quoteId || this.$store.getters['quote/quoteId']
      // Fired before the redirect, so the last step of the funnel is recorded
      // even if the customer bounces off /quote-setup immediately.
      this.trackStep('authenticated', {
        quote_id: id,
        cover_type: this.$store.getters['quote/coverType'],
      })
      // The panel is closing into a navigation, not back to the page behind
      // it, so its history entries are left for the router to replace.
      this.isNavigatingAway = true
      // Close first: releasing the body lock before the route changes leaves
      // the router free to place the new page's scroll position.
      this.$modal.hide('quick-quote')
      this.$router.push(`/quote-setup/${id}`)
      this.isRiskFactors = true
      this.showLoginForm = false
      this.showRegister = false
    },
    async continueToPersonalCoverQuoteProcess(riskFactor) {
      await this.editPersonalCoverRiskFactors(riskFactor)
      await this.createPersonalCoverQuote()
      if (this.$store.getters['user/isAuthenticated']) {
        const pathUrl = `/quote/${this.$store.getters['quote/quoteId']}/personal-cover`
        this.$router.push({ path: pathUrl, query: { ...this.$route.query } })
      } else {
        this.showLoginForm = true
      }
    },
    continueToPersonalCover() {
      this.isRiskFactors = false
      this.personalCover = true
      // await this.editPersonalCoverRiskFactors(riskFactor)
    },
    closeQuickQuote() {
      this.$modal.hide('quick-quote')
    },
    beforeOpen(event) {
      lockBodyScroll()
      const params = event.params || {}
      this.prefill = this.buildPrefill(params)
      // An explicit src from the caller wins: a landing page knows which CTA
      // was clicked, which the URL and the referrer cannot tell us.
      this.source = sanitiseSrc(params.src) || this.resolveSource()
      this.isNavigatingAway = false
      this.pushedSteps = 0
      this.isOpen = true
      this.$gtm.push({
        event: 'quote_slider',
      })
      // The panel always opens on its first step; `currentStep` is already
      // that, so the watcher will not fire for it.
      this.enterStep(this.currentStep)
    },
    // Callers supply what they know. /quote passes slider values in `prefill`;
    // a landing page passes only `bikeType`, because it has no estimator. Both
    // arrive here and are merged into one shape for step 1.
    buildPrefill(params) {
      const prefill = { ...(params.prefill || {}) }
      const bike = normaliseBikeType(prefill.bike || params.bikeType)
      if (bike) {
        prefill.bike = bike
        // Only the e-bike page answers this for the customer — a road bike can
        // still be electric, so every other type leaves the question to them.
        if (prefill.isEbike === undefined) {
          prefill.isEbike = bike === 'electric' ? true : null
        }
      }

      return Object.keys(prefill).length ? prefill : null
    },

    // The ?src= a landing page deep-linked with, falling back to the page the
    // customer came from. Attribution only — never rendered.
    resolveSource() {
      const src = sanitiseSrc(this.$route.query.src)
      if (src) return src
      if (typeof document === 'undefined' || !document.referrer) return ''
      try {
        const referrer = new URL(document.referrer)

        return referrer.hostname === window.location.hostname
          ? `ref:${referrer.pathname}`
          : referrer.hostname
      } catch (error) {
        return ''
      }
    },
    beforeClose(event) {
      this.isOpen = false
      unlockBodyScroll()
      this.showLoginForm = false
      this.showRegister = false
      this.personalCover = false
      this.isRiskFactors = true
      this.prefill = null

      // Drop the panel's own history entries so the customer leaves on the URL
      // they arrived on, unless a step is already navigating somewhere else.
      const steps = this.pushedSteps
      this.pushedSteps = 0
      if (
        !this.isNavigatingAway &&
        steps > 0 &&
        typeof window !== 'undefined'
      ) {
        window.history.go(-steps)
      }
      this.isNavigatingAway = false
    },
  },
}
</script>
